import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { container } from '@/app/container';
import { ContentRepositoryToken } from '../../di/tokens';
import type {
  CreateSubjectInput,
  ListSubjectsInput,
  RenameSubjectInput,
} from '../../domain/entities/content.entity';
import { fetchAllSubjects } from '../fetch-all-subjects';

export function useSubjects(
  input: ListSubjectsInput,
  options: { enabled?: boolean } = {},
) {
  const repository = container.resolve(ContentRepositoryToken);
  return useQuery({
    enabled: options.enabled ?? true,
    queryKey: [
      'content',
      'subjects',
      input.page,
      input.perPage,
      input.search ?? '',
      input.includeDeleted ?? false,
    ],
    queryFn: () => repository.listSubjects(input),
    staleTime: 1000 * 60 * 5,
    placeholderData: keepPreviousData,
  });
}

/**
 * O acervo inteiro, para o Mapa de assuntos. A chave cai no prefixo que as
 * mutações invalidam, então criar/renomear/apagar refaz todas as páginas. Sem
 * `keepPreviousData`: no refetch o mapa não mostra uma carga velha como atual.
 */
export function useAllSubjects() {
  const repository = container.resolve(ContentRepositoryToken);
  return useQuery({
    queryKey: ['content', 'subjects', 'all'],
    queryFn: () =>
      fetchAllSubjects((page, perPage) =>
        repository.listSubjects({ page, perPage, includeDeleted: false }),
      ),
    staleTime: 1000 * 60 * 5,
  });
}

function useInvalidateSubjects() {
  const queryClient = useQueryClient();
  return () =>
    void queryClient.invalidateQueries({ queryKey: ['content', 'subjects'] });
}

export function useCreateSubject() {
  const repository = container.resolve(ContentRepositoryToken);
  const invalidate = useInvalidateSubjects();
  return useMutation({
    mutationFn: (input: CreateSubjectInput) => repository.createSubject(input),
    onSuccess: invalidate,
  });
}

export function useRenameSubject() {
  const repository = container.resolve(ContentRepositoryToken);
  const invalidate = useInvalidateSubjects();
  return useMutation({
    mutationFn: (vars: { subjectId: string; input: RenameSubjectInput }) =>
      repository.renameSubject(vars.subjectId, vars.input),
    onSuccess: invalidate,
  });
}

export function useDeleteSubject() {
  const repository = container.resolve(ContentRepositoryToken);
  const invalidate = useInvalidateSubjects();
  return useMutation({
    mutationFn: (subjectId: string) => repository.deleteSubject(subjectId),
    onSuccess: invalidate,
  });
}
